/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserPlus, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';

import { FilterType, thinkerChartEmptyText } from '../constants';

import {
  apiSlice,
  useGetStudentOrThinkerSummaryDataQuery,
  useGetThinkerResultsForMentorQuery,
  useGetSignUpSessionByEmailQuery,
} from '../services/apiSlice';
import { useAppSelector, store } from '../stores/AppStore';
import {
  restoreFromSession,
  resetSignUp,
} from '../features/signUp/signUpSlice';
import { handleError } from '../services/ErrorService';

import Filter, { FilterValue } from '../components/Filter';
import { Loading, LoadingBlock } from '../components/Loading';
import StudentDrillDown from '../partials/StudentDrillDown';
import Welcome from '../components/Welcome';

const ThinkerScores: FC = () => {
  const navigate = useNavigate();
  const [selectedThinker, setSelectedThinker] = useState<string>();
  const currentUser = useAppSelector((state) => state.user.user);
  const signupIncomplete = currentUser?.signupIncomplete ?? false;

  // Fetch signup session if signup is incomplete
  const {
    data: signupSession,
    isLoading: isLoadingSession,
    isError: isErrorSession,
  } = useGetSignUpSessionByEmailQuery(currentUser?.email ?? '', {
    skip: !signupIncomplete || !currentUser?.email,
  });

  const getThinkerDocument = (thinkerName?: string) => {
    if (!Array.isArray(thinkerResults) || thinkerResults.length === 0) {
      return;
    }
    return thinkerResults?.find((thinker) => thinker?.name === thinkerName);
  };
  const {
    data: thinkerResults,
    isLoading: isLoadingThinkerResults,
    isFetching: isFetchingThinkerResults,
    isError: isErrorThinkerResults,
  } = useGetThinkerResultsForMentorQuery();
  const thinkerDocument = getThinkerDocument(selectedThinker);
  const accountStatus = thinkerDocument?.accountStatus;
  const accountStatusDetails = accountStatus
    ? {
        setupRequired: {
          label: 'Setup required',
          description: 'Thinker still needs to set a password.',
          className: 'bg-yellow-100 text-yellow-800',
        },
        readyToSignIn: {
          label: 'Ready to sign in',
          description:
            'Password is ready, but the Thinker has not signed in yet.',
          className: 'bg-blue-100 text-blue-800',
        },
        accountActive: {
          label: 'Account active',
          description: 'Thinker has signed in to their account.',
          className: 'bg-green-100 text-green-800',
        },
      }[accountStatus]
    : undefined;
  const {
    data: thinkerResult,
    isFetching: isFetchingThinkerResult,
    isLoading: isLoadingThinkerResult,
  } = useGetStudentOrThinkerSummaryDataQuery(
    {
      studentId: thinkerDocument?.student ?? '',
      shouldFetchTrack: true,
    },
    {
      skip: !thinkerResults || thinkerDocument === undefined,
    },
  );

  const prefetchThinkerResult = apiSlice.usePrefetch(
    'getStudentOrThinkerSummaryData',
  );

  const thinkerFilterOptions = thinkerResults?.map((r) => ({
    label: r.name,
    value: r.name,
  }));

  useEffect(() => {
    if (!Array.isArray(thinkerResults) || thinkerResults.length === 0) {
      return;
    }
    setSelectedThinker(() => thinkerResults[0].name);
  }, [thinkerResults]);

  // Track missing signup session bug in Sentry
  useEffect(() => {
    if (
      signupIncomplete &&
      !signupSession &&
      !isLoadingSession &&
      !isErrorSession
    ) {
      // This is a bug: signup flagged incomplete but no session found
      handleError(
        new Error(
          'Incomplete signup missing session - mentor has no thinkers but session not found in database',
        ),
        {
          tags: {
            mentorId: currentUser?._id ?? 'unknown',
            email: currentUser?.email ?? 'unknown',
            errorType: 'missing_signup_session',
            accountAge: currentUser?.createdAt
              ? Math.floor(
                  (Date.now() - new Date(currentUser.createdAt).getTime()) /
                    (1000 * 60 * 60 * 24),
                )
              : 'unknown',
          },
        },
      );
    }
  }, [
    signupIncomplete,
    signupSession,
    isLoadingSession,
    isErrorSession,
    currentUser,
  ]);

  const handleCompleteSignup = () => {
    if (!signupSession) {
      // Defensive: button should be disabled, but show error if clicked anyway
      toast.error(
        'Unable to find your signup session. Please check your email for a recovery link or contact help@thinktankprep.com',
      );
      return;
    }

    // Clear old sign-up state before restoring new session
    store.dispatch(resetSignUp(0));

    // Backend calculates the correct step based on actual database state
    // We trust this calculated step to ensure recovery always resumes at the right point
    store.dispatch(
      restoreFromSession({
        sessionId: signupSession.sessionId,
        currentStep: signupSession.currentStep, // Calculated by backend based on payment/mentor/thinker state
        formData: signupSession.formData,
      }),
    );
    navigate('/sign-up?recovered=true');
  };

  if (isLoadingThinkerResults) {
    return <LoadingBlock />;
  } else if (isErrorThinkerResults) {
    throw new Error('Failed to load class students data');
  }

  return (
    <div className="grid gap-2 md:gap-4">
      <div className="m-4 md:m-0">
        <Welcome />
      </div>
      <div
        className="
          relative
          grid gap-4 p-4 md:p-6
          w-full h-full items-center 
        bg-gray-100 rounded-xl
        "
      >
        <div className="absolute right-4">
          {isFetchingThinkerResults && <Loading />}
        </div>
        <h1 className="text-3xl font-medium">
          Your Thinker
          {Array.isArray(thinkerResults) && thinkerResults.length > 1
            ? 's'
            : ''}
        </h1>
        {Array.isArray(thinkerResults) && thinkerResults.length > 0 ? (
          <>
            <Filter
              filterType={'thinker' as FilterType}
              options={thinkerFilterOptions ?? []}
              selected={
                selectedThinker
                  ? {
                      label: selectedThinker,
                      value: selectedThinker,
                    }
                  : undefined
              }
              onSelect={(selectedFilter: FilterValue) => {
                setSelectedThinker(() => selectedFilter.label);
              }}
              onHover={(selectedFilter: FilterValue) => {
                prefetchThinkerResult({
                  studentId:
                    getThinkerDocument(selectedFilter.label)?.student ?? '',
                  shouldFetchTrack: true,
                });
              }}
            />
            {accountStatusDetails && (
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`rounded-full px-3 py-1 text-sm font-medium ${accountStatusDetails.className}`}
                >
                  {accountStatusDetails.label}
                </span>
                <span className="text-sm text-gray-600">
                  {accountStatusDetails.description}
                </span>
              </div>
            )}
          </>
        ) : (
          <div className="text-center p-8 bg-white rounded-lg shadow-sm">
            {isLoadingSession ? (
              // Loading session data
              <>
                <div className="flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mx-auto mb-4">
                  <UserPlus className="w-8 h-8 text-tttDefault" />
                </div>
                <h2 className="text-2xl font-medium mb-4">
                  Welcome to The Fill the Math GAPS!
                </h2>
                <div className="flex flex-col items-center gap-2">
                  <Loading />
                  <p className="text-sm text-gray-500">
                    Checking your signup progress...
                  </p>
                </div>
              </>
            ) : signupIncomplete && !signupSession ? (
              // Incomplete signup but no session found - likely a bug
              <>
                <div className="flex items-center justify-center w-16 h-16 bg-yellow-100 rounded-full mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-yellow-600" />
                </div>
                <h2 className="text-2xl font-medium mb-4">
                  Unable to Find Signup Session
                </h2>
                <p className="text-gray-600 mb-4">
                  We couldn't locate your signup progress. This might be because
                  your recovery link has expired or there was an issue with your
                  signup.
                </p>
                <p className="text-gray-600 mb-6">
                  Please check your email for our most recent recovery message,
                  or contact our support team for assistance.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <a
                    href="mailto:help@thinktankprep.com"
                    className="bg-tttDefault text-white px-6 py-3 rounded-lg hover:bg-opacity-90 transition-all font-medium"
                  >
                    Contact Support
                  </a>
                </div>
              </>
            ) : (
              // Normal state - either has session or not flagged incomplete
              <>
                <div className="flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mx-auto mb-4">
                  <UserPlus className="w-8 h-8 text-tttDefault" />
                </div>
                <h2 className="text-2xl font-medium mb-4">
                  Welcome to The Fill the Math GAPS!
                </h2>
                <p className="text-gray-600 mb-6">
                  You haven't added any Thinkers yet. Let's get started!
                </p>
                <button
                  onClick={handleCompleteSignup}
                  disabled={!signupSession}
                  className="bg-tttDefault text-white px-6 py-3 rounded-lg hover:bg-opacity-90 transition-all font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add Your Thinker
                </button>
                <p className="text-sm text-gray-500 mt-4">
                  {signupSession
                    ? 'We found your signup progress - click above to continue where you left off.'
                    : 'Check your email for a link to continue your signup, or contact help@thinktankprep.com for assistance.'}
                </p>
              </>
            )}
          </div>
        )}
        {selectedThinker && (
          <StudentDrillDown
            shouldScrollIntoView={false}
            isLoading={isLoadingThinkerResult || isFetchingThinkerResult}
            isOpen={!!thinkerResult}
            result={thinkerResult}
            chartEmptyText={thinkerChartEmptyText}
          />
        )}
      </div>
    </div>
  );
};

export default ThinkerScores;
